// imports
import { initializeApp } from 'firebase/app';
import {
  isSupported, getMessaging, getToken as getFCMToken, onMessage
} from 'firebase/messaging';
import { INotification } from '../../common/types';

// interfaces / types / consts
type NotificationReceivedCB = (notification: INotification) => void;
const firebaseConfig = JSON.parse(process.env.REACT_APP_FIREBASE_CONFIG as string);
const FIREBASE_SERVICE_URL = `${process.env.PUBLIC_URL}/service-worker.js?firebaseConfig=${encodeURIComponent(JSON.stringify(firebaseConfig))}`;
const app = initializeApp(firebaseConfig);
const messaging = getMessaging(app);

// private methods
const checkPermission = (): Promise<void> => {
  switch (Notification.permission) {
    case 'granted':
      return Promise.resolve();
    case 'denied':
      return Promise.reject(new Error('Notification permission is denied. Please allow in browser settings.'));
    default:
      return Notification.requestPermission().then((p) => {
        switch (p) {
          case 'granted':
            return Promise.resolve();
          case 'denied':
            return Promise.reject(new Error('Notification permission was denied.'));
          default: return Promise.reject(new Error('Permission dialog was closed.'));
        }
      });
  }
};

const getServiceWorkerRegistration = (): Promise<ServiceWorkerRegistration> => navigator
  .serviceWorker.getRegistrations().then((registrations) => {
    if (registrations.length && registrations[0].active) { return Promise.resolve(registrations[0]); }
    registrations.forEach((registration) => registration.unregister());
    return navigator.serviceWorker.register(FIREBASE_SERVICE_URL)
      .then((registration) => navigator.serviceWorker.ready.then(() => Promise.resolve(registration)));
  });

// exports
export const getToken = (): Promise<string> => isSupported()
  .then((supported) => (supported ? checkPermission() : Promise.reject(new Error('Push Notifications are not supported.'))))
  .then(() => getServiceWorkerRegistration())
  .then((registration) => getFCMToken(messaging, { vapidKey: process.env.REACT_APP_VAPID_KEY, serviceWorkerRegistration: registration }))
  .then((token) => (token ? Promise.resolve(token) : Promise.reject(new Error('No registration token available.'))));

export const onNotificationReceived = (notificationCB?: NotificationReceivedCB): void => {
  isSupported().then((supported) => {
    if (!supported) { return; }
    onMessage(messaging, (payload) => {
      if (payload.data) {
        const notification = payload.data as unknown as INotification;
        if (notificationCB) { notificationCB(notification); }
      }
    });
  });
};

export default { getToken, onNotificationReceived };
